import { css } from '@emotion/react';
import { ComponentPropsWithoutRef } from 'react';
import { useHistory } from 'react-router';
import BookmarkButton from '../BookmarkButton';
import StockLogo from '../StockLogo';
import { buildPageSpacing, spacing, fontStyles, colors } from '../../styles';
import Button from '../Button';
import backIcon from './back.svg';

interface Props extends ComponentPropsWithoutRef<'header'> {
  headingText: React.ReactNode;
  symbol?: string;
  category?: string;
  name?: string;
  rightAction?: React.ReactNode;
  hideBackButton?: boolean;
  onBack?: () => void;
  variant?: 'centered' | 'left-aligned';
}

const Header = ({
  headingText,
  symbol,
  category,
  name,
  rightAction,
  hideBackButton,
  onBack,
  variant = 'centered',
  ...props
}: Props) => {
  const history = useHistory();
  const isLeftAligned = variant === 'left-aligned';

  const styles = {
    header: css`
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      z-index: 102;
      background-color: ${colors.sugar};
      padding: ${buildPageSpacing(spacing.centi, 'TOP')} ${buildPageSpacing('0px', 'RIGHT')} 0
        ${buildPageSpacing('0px', 'LEFT')};
      display: flex;
      align-items: center;
      html[data-theme='dark'] & {
        background-color: ${colors.liquorice};
      }
    `,
    headerWrapper: css`
      /* Spacer height to push content below the fixed header.
       * Must match StickyHeader's spacer (spacing.hecto = 40px) for consistency. */
      margin-bottom: ${spacing.hecto};
    `,
    backWrapper: css`
      flex: 0 0 60px;
      display: flex;
      align-items: center;
      justify-content: flex-start;

      @media (max-width: 359px) {
        flex: 0 0 50px;
      }
    `,
    backLink: css`
      display: flex;
      align-items: center;
      justify-content: center;
      padding: ${spacing.milli};
      min-width: 44px;
      min-height: 44px;

      @media (max-width: 359px) {
        padding: ${spacing.micro};
      }
    `,
    backImage: css`
      vertical-align: middle;
      height: 24px;
      width: 24px;

      @media (max-width: 359px) {
        height: 22px;
        width: 22px;
      }
    `,
    headingContainer: css`
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: ${isLeftAligned ? 'flex-start' : 'center'};
      min-width: 0;
      ${isLeftAligned && hideBackButton ? `padding-left: ${spacing.deci};` : ''}
    `,
    heading: css`
      text-align: ${isLeftAligned ? 'left' : 'center'};
      margin: 0;
      color: ${colors.liquorice};
      html[data-theme='dark'] & {
        color: ${colors.sugar};
      }
      ${isLeftAligned ? fontStyles.shark : fontStyles.fatCat};
      ${isLeftAligned
        ? 'display: flex; align-items: center;'
        : 'white-space: nowrap; overflow: hidden; text-overflow: ellipsis;'}
      padding: 0;
    `,
    logoWrapper: css`
      margin-right: ${spacing.centi};
      flex-shrink: 0;
      display: flex;
      align-items: center;
    `,
    bookmarkWrapper: css`
      flex: 0 0 60px;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      @media (max-width: 359px) {
        flex: 0 0 50px;
      }
    `,
  };

  return (
    <div css={styles.headerWrapper}>
      <header css={styles.header} {...props}>
        {!(isLeftAligned && hideBackButton) && (
          <div css={styles.backWrapper}>
            {!hideBackButton && (
              <Button onClick={onBack || history.goBack} css={styles.backLink}>
                <img src={backIcon} alt="" css={styles.backImage} />
              </Button>
            )}
          </div>
        )}

        <div css={styles.headingContainer}>
          {symbol && (
            <div css={styles.logoWrapper}>
              <StockLogo symbol={symbol} size="small" />
            </div>
          )}
          <h1 css={styles.heading}>{headingText}</h1>
        </div>

        <div css={styles.bookmarkWrapper}>
          {rightAction ||
            (symbol && category && name && (
              <BookmarkButton symbol={symbol} category={category} name={name} showLabel={false} />
            ))}
        </div>
      </header>
    </div>
  );
};

export default Header;
